import { useState } from "react";

interface AlbumPlanFormProps {
  onSubmit: () => void;
}

const genreOptions = ["Dance Pop", "R&B / Soul", "Ballad", "Hip-Hop", "Synth Pop", "K-Pop Hybrid"];
const targetOptions = ["10대 팬덤", "15-24세 여성", "MZ세대", "20-30대 남성", "전 연령", "해외 K-Pop 팬"];

export default function AlbumPlanForm({ onSubmit }: AlbumPlanFormProps) {
  const [formData, setFormData] = useState({
    artist: "",
    title: "",
    genre: "",
    target: "",
    concept: "",
    trackCount: "",
    releaseDate: "",
    budget: "",
    marketingStrategy: "",
  });
  const [evaluating, setEvaluating] = useState(false);
  const [feedback, setFeedback] = useState<{ passed: boolean; score: number; message: string } | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const set = (key: string, val: string) => { setFormData((p) => ({ ...p, [key]: val })); setFeedback(null); };

  const isReady = formData.artist && formData.title && formData.concept && formData.target && formData.genre;

  const handleEvaluate = () => {
    if (!isReady) return;
    setEvaluating(true);
    setTimeout(() => {
      const conceptLen = formData.concept.length;
      const mktLen = formData.marketingStrategy.length;
      let score = 60;
      if (conceptLen > 50) score += 15;
      if (mktLen > 30) score += 15;
      if (formData.target) score += 10;
      const passed = score >= 75;
      setFeedback({
        passed,
        score,
        message: passed
          ? `✅ 승인 (${score}점): 콘셉트 방향성이 명확하고 타깃 설정이 적절합니다. 현재 시장 트렌드인 '청량 컨셉'과 잘 부합합니다. 발매 전략 수립으로 이어주세요.`
          : `❌ 보완 요청 (${score}점): 기획안이 전반적으로 부족합니다. 콘셉트 설명을 더 구체적으로 작성하고, 마케팅 전략을 추가해 주세요. 경쟁 아티스트 분석도 포함되면 좋겠습니다.`,
      });
      setEvaluating(false);
    }, 2200);
  };

  const handleSubmit = () => {
    setSubmitted(true);
    setTimeout(() => { onSubmit(); }, 1500);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-1 mb-3">
        <h3 className="text-[#111111] font-semibold text-sm">앨범 기획서 작성</h3>
        <p className="text-[#5A5E72] text-xs mt-0.5">가상 총괄 프로듀서(AI)가 트렌드 분석 후 피드백을 드립니다</p>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[#5A5E72] text-[10px] block mb-1">아티스트명 <span className="text-[#E8375F]">*</span></label>
            <input value={formData.artist} onChange={(e) => set("artist", e.target.value)}
              placeholder="예: 루나&스타즈" className="w-full bg-[#252830] text-white text-xs rounded-lg px-3 py-2 outline-none placeholder-[#5A5E72] border border-transparent focus:border-[#E8375F]/40" />
          </div>
          <div>
            <label className="text-[#5A5E72] text-[10px] block mb-1">앨범 타이틀 <span className="text-[#E8375F]">*</span></label>
            <input value={formData.title} onChange={(e) => set("title", e.target.value)}
              placeholder="예: Starlight EP" className="w-full bg-[#252830] text-white text-xs rounded-lg px-3 py-2 outline-none placeholder-[#5A5E72] border border-transparent focus:border-[#E8375F]/40" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[#5A5E72] text-[10px] block mb-1">주요 장르 <span className="text-[#E8375F]">*</span></label>
            <select value={formData.genre} onChange={(e) => set("genre", e.target.value)}
              className="w-full bg-[#252830] text-white text-xs rounded-lg px-3 py-2 outline-none border border-transparent focus:border-[#E8375F]/40 cursor-pointer">
              <option value="">선택...</option>
              {genreOptions.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[#5A5E72] text-[10px] block mb-1">주요 타깃층 <span className="text-[#E8375F]">*</span></label>
            <select value={formData.target} onChange={(e) => set("target", e.target.value)}
              className="w-full bg-[#252830] text-white text-xs rounded-lg px-3 py-2 outline-none border border-transparent focus:border-[#E8375F]/40 cursor-pointer">
              <option value="">선택...</option>
              {targetOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[#5A5E72] text-[10px] block mb-1">트랙 수</label>
            <input type="number" value={formData.trackCount} onChange={(e) => set("trackCount", e.target.value)}
              placeholder="예: 6" className="w-full bg-[#252830] text-white text-xs rounded-lg px-3 py-2 outline-none placeholder-[#5A5E72] border border-transparent focus:border-[#E8375F]/40" />
          </div>
          <div>
            <label className="text-[#5A5E72] text-[10px] block mb-1">발매 예정일</label>
            <input type="date" value={formData.releaseDate} onChange={(e) => set("releaseDate", e.target.value)}
              className="w-full bg-[#252830] text-white text-xs rounded-lg px-3 py-2 outline-none border border-transparent focus:border-[#E8375F]/40" />
          </div>
        </div>

        <div>
          <label className="text-[#5A5E72] text-[10px] block mb-1">앨범 콘셉트 <span className="text-[#E8375F]">*</span></label>
          <textarea value={formData.concept} onChange={(e) => set("concept", e.target.value)}
            placeholder="앨범의 주제, 감성, 핵심 메시지, 시각적 방향성을 상세히 서술하세요..."
            maxLength={500} rows={3}
            className="w-full bg-[#252830] text-white text-xs rounded-lg px-3 py-2 outline-none placeholder-[#5A5E72] border border-transparent focus:border-[#E8375F]/40 resize-none" />
        </div>

        <div>
          <label className="text-[#5A5E72] text-[10px] block mb-1">마케팅 전략</label>
          <textarea value={formData.marketingStrategy} onChange={(e) => set("marketingStrategy", e.target.value)}
            placeholder="SNS 전략, 콜라보 계획, 프로모션 방향 등을 서술하세요..."
            maxLength={500} rows={2}
            className="w-full bg-[#252830] text-white text-xs rounded-lg px-3 py-2 outline-none placeholder-[#5A5E72] border border-transparent focus:border-[#E8375F]/40 resize-none" />
        </div>

        {feedback && (
          <div className={`p-3 rounded-xl border text-xs leading-relaxed ${
            feedback.passed ? "bg-[#00C896]/10 border-[#00C896]/30 text-[#00C896]" : "bg-[#E8375F]/10 border-[#E8375F]/30 text-[#E8375F]"
          }`}>
            <div className="flex items-center gap-1.5 mb-1">
              <i className="ri-robot-line"></i>
              <span className="font-semibold">AI 총괄 프로듀서 피드백</span>
              <span className={`ml-auto text-xs font-bold ${feedback.passed ? "text-[#00C896]" : "text-[#E8375F]"}`}>
                {feedback.score}점
              </span>
            </div>
            {feedback.message}
          </div>
        )}

        {submitted && (
          <div className="p-3 rounded-xl bg-[#00C896]/10 border border-[#00C896]/30 text-[#00C896] text-xs text-center">
            <i className="ri-check-double-line mr-1"></i>기획서가 제출되었습니다!
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-3 pt-3 border-t border-[#252830]">
        <button onClick={handleEvaluate} disabled={!isReady || evaluating}
          className="flex-1 py-2 border border-[#FF9F43]/50 text-[#FF9F43] text-xs rounded-lg hover:bg-[#FF9F43]/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap">
          {evaluating ? <span><i className="ri-loader-4-line animate-spin mr-1"></i>AI 분석 중...</span>
            : <span><i className="ri-robot-line mr-1"></i>AI 평가 요청</span>}
        </button>
        <button onClick={handleSubmit} disabled={!feedback?.passed || submitted}
          className="flex-1 py-2 bg-[#E8375F] hover:bg-[#d42e52] text-white text-xs rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-send-plane-line mr-1"></i>기획서 제출
        </button>
      </div>
    </div>
  );
}
